/**
 * Fragment API Service
 *
 * API client for ContentFragment CRUD operations and visibility checks.
 * Integrates with backend FastAPI endpoints at /fragments.
 */

import { api } from './apiClient';

// ============================================================
// Types
// ============================================================

export interface ContentFragment {
  id: number;
  code: string;
  role: string | null;
  status: number;
  created_at: string;
  updated_at: string;
}

export interface ContentFragmentCreate {
  code: string;
  role?: string | null;
  status?: number;
}

export interface ContentFragmentUpdate {
  code?: string;
  role?: string | null;
  status?: number;
}

export interface FragmentVisibilityRequest {
  fragment_code: string;
  user_role: string;
}

export interface FragmentVisibilityResponse {
  fragment_code: string;
  is_visible: boolean;
  reason?: string;
}

export interface PaginationMeta {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface FragmentListResponse {
  data: ContentFragment[];
  pagination: PaginationMeta;
}

// ============================================================
// API Functions
// ============================================================

/**
 * List fragments with pagination and optional status filter.
 */
export async function listFragments(params: {
  page?: number;
  page_size?: number;
  status_filter?: number;
}): Promise<FragmentListResponse> {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.page_size) queryParams.append('page_size', params.page_size.toString());
  if (params.status_filter !== undefined) {
    queryParams.append('status_filter', params.status_filter.toString());
  }

  return api.get<FragmentListResponse>(`/content/fragments?${queryParams.toString()}`);
}

/**
 * Get a specific fragment by ID.
 */
export async function getFragment(id: number): Promise<ContentFragment> {
  return api.get<ContentFragment>(`/content/fragments/${id}`);
}

/**
 * Get a specific fragment by its unique code.
 *
 * Throws an axios 404 when the fragment does not exist — callers should
 * catch and treat 404 as "not found" (signals create mode in the modal).
 *
 * @since 2026-05-08 [TMF9-54249] — substitui list+find que dependia de
 * paginacao 1000 itens e ficava instavel quando o response demorava ou
 * falhava (botao Salvar travava porque dependia de !fragmentsResponse).
 */
export async function getFragmentByCode(code: string): Promise<ContentFragment> {
  return api.get<ContentFragment>(`/content/fragments/by-code/${encodeURIComponent(code)}`);
}

/**
 * Create a new fragment.
 */
export async function createFragment(
  data: ContentFragmentCreate
): Promise<ContentFragment> {
  return api.post<ContentFragment>('/content/fragments', data);
}

/**
 * Update an existing fragment.
 */
export async function updateFragment(
  id: number,
  data: ContentFragmentUpdate
): Promise<ContentFragment> {
  return api.put<ContentFragment>(`/content/fragments/${id}`, data);
}

/**
 * Delete a fragment.
 */
export async function deleteFragment(id: number): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/content/fragments/${id}`);
}

/**
 * Check if a fragment should be visible for a specific user role.
 */
export async function checkFragmentVisibility(
  data: FragmentVisibilityRequest
): Promise<FragmentVisibilityResponse> {
  return api.post<FragmentVisibilityResponse>('/content/fragments/check-visibility', data);
}
